import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import config from '../config';

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        try {
            const savedUser = localStorage.getItem('user');
            return (savedUser && savedUser !== 'undefined') ? JSON.parse(savedUser) : null;
        } catch (error) {
            console.error('Error parsing user from localStorage:', error);
            localStorage.removeItem('user');
            return null;
        }
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState(null);
    const fetchTimeoutId = useRef(null);
    const fetchInProgress = useRef(false);
    const tokenValidationPaused = useRef(false); // Ref to pause token validation during critical operations

    // Clear user state and reset all related data
    const clearUserState = useCallback(() => {
        setUser(null);
        setError(null);
        setIsInitialized(true); // Mark as initialized so we don't keep trying to fetch
        setIsLoading(false);
        localStorage.removeItem('user');
        sessionStorage.clear(); // Clear session storage

        if (fetchTimeoutId.current) {
            clearTimeout(fetchTimeoutId.current);
            fetchTimeoutId.current = null;
        }
    }, []);

    // Fetch user data from the backend
    const fetchUser = useCallback(async (force = false) => {
        const token = Cookies.get('authToken');

        if (!token) {
            clearUserState();
            return null;
        }

        if (!force && isInitialized) {
            return null;
        }

        // Cancel previous fetch attempt
        if (fetchTimeoutId.current) {
            clearTimeout(fetchTimeoutId.current);
        }

        if (fetchInProgress.current) {
            return null;
        }

        return new Promise((resolve) => {
            fetchTimeoutId.current = setTimeout(async () => {
                try {
                    fetchInProgress.current = true;
                    setError(null);

                    const response = await fetch(`${config.backendUrl}/users/me/`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Token ${token}`
                        }
                    });

                    if (response.status === 403 || response.status === 401) {
                        // Token is invalid or expired
                        clearUserState();
                        Cookies.remove('authToken');
                        resolve(null);
                        return;
                    }

                    if (!response.ok) {
                        throw new Error('Failed to fetch user');
                    }

                    const data = await response.json();
                    setUser(data);
                    localStorage.setItem('user', JSON.stringify(data));
                    setIsInitialized(true);
                    resolve(data);
                } catch (error) {
                    console.error('Error fetching user:', error);
                    clearUserState();
                    resolve(null);
                } finally {
                    fetchInProgress.current = false;
                    setIsLoading(false);
                    fetchTimeoutId.current = null;
                }
            }, force ? 0 : 500);
        });
    }, [clearUserState, isInitialized]);

    // Refresh the authentication token (NOT NEEDED - Django tokens don't expire)
    // This function is kept for backward compatibility but does nothing
    const refreshAuthToken = useCallback(async () => {
        // Skip validation if paused (e.g., during form submission)
        if (tokenValidationPaused.current) {

            return;
        }

        // Django's TokenAuthentication tokens don't expire by default
        // No need to refresh, just verify the token is still valid
        const token = Cookies.get('authToken');
        if (!token) return;

        try {
            // Just validate the token by making a lightweight request
            const response = await fetch(`${config.backendUrl}/users/me/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Token ${token}`,
                },
            });

            if (response.status === 401 || response.status === 403) {
                // Token is invalid - clear state

                clearUserState();
                Cookies.remove('authToken');
            } else if (response.ok) {
                // Token is still valid

            }
            // If other error (500, network issue), do nothing - don't log user out
        } catch (error) {
            // Network error - don't log user out, they might be offline temporarily
        }
    }, [clearUserState]);

    // Login function to set user data
    const login = useCallback(async (userData) => {
        try {
            // Clear any existing user data first
            clearUserState();

            // Set new user data
            setUser(userData);
            localStorage.setItem('user', JSON.stringify(userData));
            setIsInitialized(true);
            setIsLoading(false);

            // Check for stored intended route and redirect after login
            const intendedRoute = sessionStorage.getItem('intendedRoute');
            if (intendedRoute) {
                sessionStorage.removeItem('intendedRoute');
                // Use React Router's navigate instead of window.location for SPA navigation
                setTimeout(() => {
                    window.location.href = intendedRoute;
                }, 100); // Small delay to ensure state updates
            }

            return userData;
        } catch (error) {
            clearUserState();
            throw error;
        }
    }, [clearUserState]);

    // Redirect to login with intended route stored
    const redirectToLogin = useCallback((intendedRoute) => {
        // Store the intended route
        if (intendedRoute) {
            sessionStorage.setItem('intendedRoute', intendedRoute);
        }
        // Navigate to login using window.location for full page reload
        window.location.href = '/login';
    }, []);

    // Logout function to clear user data and invalidate the session
    const logout = useCallback(async () => {
        const token = Cookies.get('authToken');
        
        // Always clear local state, even if API call fails
        const clearLocalData = () => {
            Cookies.remove('authToken');
            clearUserState();
            fetchInProgress.current = false;
        };

        try {
            if (token) {

                const response = await fetch(`${config.backendUrl}/users/logout/`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Token ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {

                } else if (response.status === 401 || response.status === 403) {

                } else {

                }
            }
        } catch (error) {
            console.error('Logout API error:', error);
            // Continue with local logout even if API call fails
        } finally {
            // Always clear local data regardless of API response
            clearLocalData();
            
            // Redirect to home page
            window.location.href = '/';
        }
    }, [clearUserState]);

    // Force refresh user data
    const refreshUser = useCallback(async () => {
        await fetchUser(true);
    }, [fetchUser]);

    // Automatically validate the token periodically (once per hour)
    // This is just to catch edge cases where token becomes invalid
    useEffect(() => {
        const intervalId = setInterval(() => {
            refreshAuthToken();
        }, 60 * 60 * 1000); // Validate every 60 minutes (not critical, tokens don't expire)

        return () => clearInterval(intervalId);
    }, [refreshAuthToken]);

    useEffect(() => {
        const token = Cookies.get('authToken');
        if (token && !isInitialized) {
            fetchUser(true);
        } else if (!token) {
            // If no token, immediately set loading to false
            setIsLoading(false);
            setIsInitialized(true);
        }

        // Listen for userStateChanged event (triggered after verification/login)
        const handleUserStateChanged = () => {
            const newToken = Cookies.get('authToken');
            if (newToken) {
                fetchUser(true);
            }
        };
        
        window.addEventListener('userStateChanged', handleUserStateChanged);

        return () => {
            if (fetchTimeoutId.current) {
                clearTimeout(fetchTimeoutId.current);
            }
            fetchInProgress.current = false;
            window.removeEventListener('userStateChanged', handleUserStateChanged);
        };
    }, [fetchUser, isInitialized]);

    // Functions to pause and resume token validation during critical operations
    const pauseTokenValidation = useCallback(() => {
        tokenValidationPaused.current = true;

    }, []);

    const resumeTokenValidation = useCallback(() => {
        tokenValidationPaused.current = false;

    }, []);

    const value = {
        user,
        setUser,
        isLoading,
        fetchUser,
        refreshUser,
        refreshAuthToken, // Add this to the context
        pauseTokenValidation, // Pause token validation
        resumeTokenValidation, // Resume token validation
        isInitialized,
        error,
        login,
        logout,
        redirectToLogin
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};